import { ScrollArea } from "@/components/ui/scroll-area";
import type { SidebarProps } from "@/types/layout";
import { NavigationItem } from "./NavigationItem";
import { StoreListItem } from "./StoreListItem";
import HomeIcon from "./HomeIcon";
import ShopIcon from "./ShopIcon";
import LiveIcon from "./LiveIcon";
import WalletIcon from "./WalletIcon";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FollowingDialog } from "@/components/following";
import { useAuth } from "@/contexts";

interface SidebarPropsExtended extends SidebarProps {
  isLoading?: boolean;
}

export function Sidebar({
  followingStores,
  activeRoute = "home",
  onStoreClick,
  onNavigate,
  isOpen = true,
  isLoading = false,
}: SidebarPropsExtended) {
  const [open, setOpen] = useState(false);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const handleViewAllClick = () => {
    // console.log("View all clicked");
  };

  return (
    <aside
      className={`sidebar-container h-full overflow-hidden transition-all duration-300 
        ${isOpen ? "w-[272px]" : "w-0 lg:w-[72px]"}
        fixed lg:relative left-0 top-0 z-50 lg:z-auto
        ${isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
      `}
    >
      <div className="flex flex-col h-full">
        {/* Navigation Menu */}
        <div className="flex flex-col gap-[12px] p-5 pb-[12px] border-b border-sidebar-border">
          <NavigationItem
            icon={
              <HomeIcon
                width={17}
                height={17}
                color={
                  activeRoute === "home" ? "#ffffff" : "rgba(0, 0, 0, 0.50)"
                }
              />
            }
            label="home"
            isActive={activeRoute === "home"}
            onClick={() => onNavigate?.("home")}
            isCollapsed={!isOpen}
          />
          <NavigationItem
            icon={
              <ShopIcon
                width={17}
                height={17}
                color={
                  activeRoute === "shop" ? "#ffffff" : "rgba(0, 0, 0, 0.50)"
                }
              />
            }
            label="shop"
            isActive={activeRoute === "shop"}
            onClick={() => onNavigate?.("shop")}
            isCollapsed={!isOpen}
          />
          <NavigationItem
            icon={
              <LiveIcon
                width={15}
                height={11}
                color={
                  activeRoute === "live" ? "#ffffff" : "#ff0000"
                }
              />
            }
            label="live"
            isActive={activeRoute === "live"}
            onClick={() => onNavigate?.("live")}
            isCollapsed={!isOpen}
          />
          {isAuthenticated && (
            <NavigationItem
              icon={
                <WalletIcon
                  width={17}
                  height={17}
                  color={activeRoute === "wallet" ? "#ffffff" : "rgba(0,0,0,0.50)"}
                />
              }
              label="my wallet"
              isActive={activeRoute === "wallet"}
              onClick={() => navigate("/wallet")}
              isCollapsed={!isOpen}
            />
          )}
        </div>

        {/* Following Section - Only show when expanded */}
        {isOpen && (
          <div className="flex flex-col flex-1 overflow-hidden">
            <div className="px-5 pt-[24px] pb-[12px]">
              <h3 className="text-[15px] font-lexend font-normal tracking-[-0.22px] capitalize text-sidebar-heading">
                following
              </h3>
            </div>

            <ScrollArea className="flex-1 px-5">
              <div className="flex flex-col gap-[12px] pb-4">
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400"></div>
                  </div>
                ) : followingStores.length === 0 ? (
                  <div className="text-center py-8 text-sm text-gray-500">
                    No stores followed yet
                  </div>
                ) : (
                  <>
                   {followingStores.slice(0, 5).map((store) => (
                    <StoreListItem
                      key={store.id}
                      store={store}
                      onClick={() => onStoreClick?.(store.id)}
                    />
                  ))}

                    <FollowingDialog
                      open={open}
                      onOpenChange={setOpen}
                      trigger={
                        <div className="px-5 py-4 border-t border-sidebar-border">
                          <button
                            onClick={handleViewAllClick}
                            className="text-[15px] font-lexend font-semibold tracking-[-0.22px] capitalize text-sidebar-link-active hover:underline cursor-pointer"
                          >
                            view all
                          </button>
                        </div>
                      }
                    />
                  </>
                )}
              </div>
            </ScrollArea>
          </div>
        )}
         {/* Sign Up and Login Buttons (shown only if not authenticated) */}
         {!isAuthenticated && (
              <div className="flex lg:hidden flex-col gap-2 px-5 pb-4">
                <a
                  href="/signup"
                  className="border border-zinc-500 text-center text-zinc-500 text-sm px-4 py-1.5 rounded-md font-medium transition-colors hover:border-gray-400 hover:bg-gray-50"
                >
                  Sign up
                </a>
                <a
                  href="/login"
                  className="bg-[#16783A] text-white text-center text-sm px-4 py-1.5 rounded-md font-medium transition-colors hover:bg-[#11642f]"
                >
                  Login
                </a>
              </div>
            )}
      </div>
    </aside>
  );
}